import { Vue, Component } from "vue-property-decorator";
import { Getter } from "vuex-class";
import AdcontrolProfile from "../../../models/AdcontrolProfile";
import {
  CustomReportParameters,
  CustomReportWithTemplateParameters,
} from "@/mixins/ReportBuilder/ReportBuilderMixin";
import { API_ENDPOINT } from "@/constants";
import {
  coalesceArrayToNull,
  removeNullProperties,
} from "@/services/Utilities";

const HTTP_REQUEST_ACTION = "http/request";

export enum ReportExportTags {
  CUSTOM = "post_export",
  CUSTOM_WITH_TEMPLATE = "post_export_with_template",
}

@Component({})
export default class ReportExportMixin extends Vue {
  @Getter("profile") mixinProfile!: AdcontrolProfile;

  // http/request resolves with the response body and rejects on HTTP errors,
  // so success/failure is signalled by resolve/reject.
  postExport(params: CustomReportParameters): Promise<unknown> {
    const type = this.mixinProfile.accountType;
    const url =
      API_ENDPOINT +
      `/${this.mixinProfile.slug}/${type}/competitors/custom.json`;

    let postData = {
      competitors: coalesceArrayToNull(params.broadcasters?.map((e) => e.id)),
      agencies: coalesceArrayToNull(params.agencies?.map((e) => e.id)),
      companies: coalesceArrayToNull(
        params.products?.filter((e) => e.type == "company").map((e) => e.id)
      ),
      brands: coalesceArrayToNull(
        params.products?.filter((e) => e.type == "brand").map((e) => e.id)
      ),
      products: coalesceArrayToNull(
        params.products?.filter((e) => e.type == "product").map((e) => e.id)
      ),
      sectors: coalesceArrayToNull(
        params.sectors?.filter((e) => e.type == "sector").map((e) => e.id)
      ),
      categories: coalesceArrayToNull(
        params.sectors?.filter((e) => e.type == "category").map((e) => e.id)
      ),
      classes: coalesceArrayToNull(
        params.sectors?.filter((e) => e.type == "class").map((e) => e.id)
      ),
      starts_at: params.starts_at,
      ends_at: params.ends_at,
      comparison_starts_at: params.compare_starts_at,
      comparison_ends_at: params.compare_ends_at,
      type: params.unit,
      temporal_detail: params.granularity_enabled,
      quota: params.quotas,
      raw: params.output_type === "raw",
      inclusion: params.inclusion,
      time_slots_filter:
        (params.time_slots?.length ?? 0) > 0 ? params.time_slots : null,
      granularity: params.granularity,
      week_type: "eu",
      time_slots: params.time_slots_enabled,
      show_special_initiatives: params.special_initiatives_detail,
      auditel_target: params.auditel_target?.id ?? null,
      format_detail: params.format_detail,
      show_vod_devices: params.show_vod_devices,
      apply_client_database: params.apply_client_database,
      attach_sector_category_class: params.attach_sector_category_class,
    };

    postData = removeNullProperties(postData);

    return this.$store.dispatch(HTTP_REQUEST_ACTION, {
      tag: ReportExportTags.CUSTOM,
      url,
      method: "POST",
      data: { ...postData },
    });
  }

  postExportWithTemplate(
    params: CustomReportWithTemplateParameters
  ): Promise<unknown> {
    const type = this.mixinProfile.accountType;
    const url =
      API_ENDPOINT +
      `/${this.mixinProfile.slug}/${type}/competitors/custom_with_template.json`;

    let postData = {
      template_id: params.template_id,
      starts_at: params.starts_at,
      ends_at: params.ends_at,
      comparison_starts_at: params.compare_starts_at,
      comparison_ends_at: params.compare_ends_at,
      time_slots_filter:
        (params.time_slots?.length ?? 0) > 0 ? params.time_slots : null,
      time_slots: params.time_slots_enabled,
    };

    postData = removeNullProperties(postData);

    const data: Record<string, unknown> = { ...postData };
    if (params.granularity !== undefined) {
      data.granularity = params.granularity;
      data.temporal_detail = params.granularity !== null;
    }

    return this.$store.dispatch(HTTP_REQUEST_ACTION, {
      tag: ReportExportTags.CUSTOM_WITH_TEMPLATE,
      url,
      method: "POST",
      data,
    });
  }
}
